import { useCallback, useEffect, useRef, useState } from "react";
import { Button, Spinner, StatusBadge } from "@leylion/design-system";
import { PlatformDiagnosticAlert } from "./PlatformDiagnosticAlert";
import { toPlatformDiagnostic, type PlatformLanguage } from "./platform-diagnostics";
import { getPlatformOperations, type OperationsDependency, type PlatformOperationsSnapshot } from "./platform-operations-client";
import "./platform-operations.css";

export const operationsRefreshEvent = "leylion-platform-operations-refresh";
const staleAfterMilliseconds = 60_000;

function readClock() {
  return { elapsed: performance.now(), wall: Date.now() };
}

export function PlatformOperationsPanel({ token, language }: { token: string; language: PlatformLanguage }) {
  const ar = language === "ar";
  const [reading, setReading] = useState<{
    snapshot: PlatformOperationsSnapshot;
    startedAt: ReturnType<typeof readClock>;
  } | null>(null);
  const snapshot = reading?.snapshot ?? null;
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [now, setNow] = useState(readClock);
  const request = useRef<AbortController | null>(null);

  const refresh = useCallback(async () => {
    if (request.current) return;
    const controller = new AbortController();
    request.current = controller;
    const startedAt = readClock();
    setBusy(true);
    const timeout = window.setTimeout(() => controller.abort(), 10_000);
    try {
      const value = await getPlatformOperations(token, controller.signal);
      if (request.current !== controller || controller.signal.aborted) return;
      setReading({ snapshot: value, startedAt });
      setError(null);
      setNow(readClock());
    } catch (caught) {
      if (request.current === controller) setError(caught);
    } finally {
      window.clearTimeout(timeout);
      if (request.current === controller) {
        request.current = null;
        setBusy(false);
      }
    }
  }, [token]);

  useEffect(() => {
    setReading(null);
    setError(null);
    void refresh();
    const onRefresh = () => { void refresh(); };
    window.addEventListener(operationsRefreshEvent, onRefresh);
    // This clock updates freshness labels only; there is no network polling.
    const clock = window.setInterval(() => setNow(readClock()), 5_000);
    return () => {
      const controller = request.current;
      request.current = null;
      controller?.abort();
      window.clearInterval(clock);
      window.removeEventListener(operationsRefreshEvent, onRefresh);
    };
  }, [refresh]);

  // Age the uncached, on-demand response using only clocks from this browser.
  // Monotonic time survives clock corrections; wall time also counts device sleep.
  const stale = reading !== null && (error !== null
    || Math.max(now.elapsed - reading.startedAt.elapsed, now.wall - reading.startedAt.wall) >= staleAfterMilliseconds);
  const status = busy ? (ar ? "جارٍ القراءة" : "Reading")
    : !snapshot ? (ar ? "غير متاح" : "Unavailable")
      : stale ? (ar ? "بيانات قديمة" : "Stale")
        : snapshot.status === "partial" ? (ar ? "بيانات جزئية" : "Partial data")
          : (ar ? "قراءة حديثة" : "Recent snapshot");
  const traffic = snapshot?.traffic;
  const number = (value: number | null | undefined) => value === null || value === undefined
    ? "—" : value.toLocaleString(ar ? "ar-AE" : "en-AE", { maximumFractionDigits: 4 });

  return (
    <section className="platform-operations" data-testid="platform-operations" aria-labelledby="platform-operations-title">
      <div className="platform-operations__header">
        <div>
          <h2 id="platform-operations-title">{ar ? "حالة التشغيل الحالية" : "Current operations"}</h2>
          <p>{ar ? "قراءة يدوية من نسخة API التي أجابت على الطلب. تصبح القراءة قديمة بعد دقيقة." : "Manual snapshot from the API instance answering this request. Readings become stale after one minute."}</p>
        </div>
        <Button variant="secondary" type="button" aria-disabled={busy} onClick={() => void refresh()}>
          {ar ? "تحديث التشغيل" : "Refresh operations"}
        </Button>
      </div>

      <div className="platform-operations__status" role="status" aria-live="polite" data-testid="operations-freshness">
        <StatusBadge tone={stale || (!busy && !snapshot) ? "warning" : "info"}>{status}</StatusBadge>
        {snapshot ? <div>
          <span>{ar ? "آخر قراءة ناجحة: " : "Last successful read: "}</span>
          <time dateTime={snapshot.generatedAt}>{new Date(snapshot.generatedAt).toLocaleString(ar ? "ar-AE" : "en-AE")}</time>
          <small><bdi dir="ltr">{new Date(snapshot.generatedAt).toISOString()} (UTC)</bdi></small>
        </div> : null}
      </div>

      {error !== null ? <PlatformDiagnosticAlert diagnostic={toPlatformDiagnostic(error, language)} language={language} /> : null}
      {busy && !snapshot ? <div className="platform-operations__empty"><Spinner label={ar ? "جارٍ قراءة التشغيل" : "Reading operations"} /></div> : null}
      {!busy && !snapshot ? <p className="platform-operations__empty">{ar ? "تعذر الحصول على قراءة. أعد المحاولة باستخدام تحديث التشغيل." : "A snapshot could not be obtained. Use Refresh operations to try again."}</p> : null}

      {snapshot ? <div className="platform-operations__sections" data-stale={stale || busy ? "true" : "false"}>
        {stale ? <p className="platform-operations__note">{ar ? "القيم أدناه من قراءة سابقة، ولا تؤكد الحالة الحالية." : "Values below are from a previous reading and do not confirm the current state."}</p> : null}
        <article className="platform-panel platform-operations__card">
          <h3>{ar ? "نسخة التشغيل" : "Runtime identity"}</h3>
          <dl className="platform-operations__facts">
            {([
              [ar ? "الإصدار" : "Version", snapshot.runtime.version],
              [ar ? "البناء" : "Build", snapshot.runtime.buildNumber],
              [ar ? "البيئة" : "Environment", snapshot.runtime.environment]
            ] as const).map(([name, value]) => <div key={name}><dt>{name}</dt><dd><bdi dir="ltr">{value ?? (ar ? "غير معروف" : "Unknown")}</bdi></dd></div>)}
          </dl>
          <p>{ar ? "هذه هوية Runtime المعلنة؛ ولا تثبت نشر الإنتاج أو اعتماده." : "This is the reported runtime identity; it does not establish production deployment or verification."}</p>
        </article>

        <article className="platform-panel platform-operations__card">
          <h3>{ar ? "جاهزية الاعتمادات" : "Dependency readiness"}</h3>
          <div className="platform-operations__dependencies">
            <Dependency label={ar ? "قاعدة البيانات" : "Database"} value={snapshot.dependencies.database} ar={ar} historical={stale || busy} />
            <Dependency label="Redis" value={snapshot.dependencies.redis} ar={ar} historical={stale || busy} />
          </div>
          <p>{ar ? "فحص اتصال قاعدة البيانات وRedis فقط، وليس فحصًا لكل وظائف المنصة." : "Database and Redis connection checks only, not a check of every platform function."}</p>
        </article>

        <article className="platform-panel platform-operations__card">
          <h3>{ar ? "الطلبات · آخر 15 دقيقة" : "Requests · last 15 minutes"}</h3>
          <p data-testid="operations-traffic-state">{traffic?.status === "no-samples"
            ? (ar ? "لا توجد عينات" : "No samples")
            : traffic?.status === "unavailable" ? (ar ? "المؤشرات غير متاحة" : "Metrics unavailable")
              : (ar ? "عينات الطلبات المكتملة" : "Completed request samples")}</p>
          <dl className="platform-operations__facts platform-operations__facts--metrics">
            <div><dt>{ar ? "الطلبات" : "Requests"}</dt><dd>{number(traffic?.requestCount)}</dd></div>
            <div><dt>{ar ? "أخطاء الخادم (5xx)" : "Server errors (5xx)"}</dt><dd>{number(traffic?.serverErrorCount)}</dd></div>
            <div><dt>{ar ? "نسبة 5xx" : "5xx rate"}</dt><dd>{number(traffic?.serverErrorRatePercent)}{traffic?.serverErrorRatePercent != null ? "%" : ""}</dd></div>
            <div><dt>{ar ? "زمن P95 (مللي ثانية)" : "P95 latency (ms)"}</dt><dd>{number(traffic?.p95LatencyMilliseconds)}</dd></div>
          </dl>
          <p>{ar
            ? `تخص عملية API الحالية فقط، بحد أقصى ${number(traffic?.maximumSamples)} عينة محفوظة، وتشمل طلبات فحص الصحة. تُصفّر عند إعادة التشغيل وقد تغطي أقل من 15 دقيقة عند بلوغ الحد. ليست مؤشرات مجمعة لكل النسخ أو سجل SLO.`
            : `Current API process only, up to ${number(traffic?.maximumSamples)} retained samples, including health checks. Resets on restart and may cover less than 15 minutes when capped. These are not fleet aggregates or historical SLOs.`}</p>
        </article>
      </div> : null}
    </section>
  );
}

function Dependency({ label, value, ar, historical }: { label: string; value: OperationsDependency; ar: boolean; historical: boolean }) {
  const labels = {
    ready: ar ? "جاهز" : "Ready",
    "not-ready": ar ? "غير جاهز" : "Not ready",
    disabled: ar ? "معطّل بالإعدادات" : "Disabled by configuration",
    unavailable: ar ? "تعذر الفحص" : "Check unavailable"
  };
  return <div>
    <strong>{label}</strong>
    <StatusBadge tone={historical || value.status === "disabled" ? "neutral" : value.status === "ready" ? "success" : "warning"}>
      {labels[value.status]}
    </StatusBadge>
    {value.code ? <small><bdi dir="ltr">{value.code}</bdi></small> : null}
  </div>;
}
